// frontend/src/pages/VerifyOtp.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "../axiosConfig";

const RESEND_COOLDOWN_SEC = 30;
const STORAGE_SMS_LAST_SENT = "smsLastSentAt"; // guard duplicate init + spam

export default function VerifyOtp() {
  const navigate = useNavigate();

  // ======= Shared state =======
  const [email, setEmail] = useState("");

  // ======= Email OTP state =======
  const [emailOtp, setEmailOtp] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [submittingEmail, setSubmittingEmail] = useState(false);
  const [resendLoadingEmail, setResendLoadingEmail] = useState(false);
  const [cooldownEmail, setCooldownEmail] = useState(0);

  // ======= Mobile OTP state =======
  const [mobileOtp, setMobileOtp] = useState("");
  const [mobileMsg, setMobileMsg] = useState("");
  const [mobileVerified, setMobileVerified] = useState(false);
  const [resendLoadingSms, setResendLoadingSms] = useState(false);
  const [cooldownSms, setCooldownSms] = useState(0);

  // ======= Combined verify button state =======
  const [verifyingBoth, setVerifyingBoth] = useState(false);

  // ======= Success banner + auto-redirect =======
  const [allVerified, setAllVerified] = useState(false);
  const [redirectIn, setRedirectIn] = useState(5);

  // StrictMode guard so the init SMS send does not fire twice in dev
  const smsInitSentRef = useRef(false);

  // ======= Load email and (best-effort) prepare mobile =======
  useEffect(() => {
    const stored = localStorage.getItem("signupEmail");
    if (!stored) {
      navigate("/signup");
      return;
    }
    setEmail(stored);

    // Best-effort: send initial SMS OTP once, and not more often than cooldown window
    if (!smsInitSentRef.current) {
      smsInitSentRef.current = true;

      const lastAt = Number(localStorage.getItem(STORAGE_SMS_LAST_SENT) || 0);
      const now = Date.now();
      const elapsed = Math.floor((now - lastAt) / 1000);
      if (elapsed >= RESEND_COOLDOWN_SEC) {
        (async () => {
          try {
            const { data } = await axios.post("/send-mobile-otp", { email: stored });
            if (data?.mobileE164) localStorage.setItem("mobileE164", data.mobileE164);
            localStorage.setItem(STORAGE_SMS_LAST_SENT, String(Date.now()));
          } catch (e) {
            // Do not surface to user; keep UI identical
            console.warn("send-mobile-otp (init) failed:", e?.response?.data || e?.message);
            // If rate-limited, start a cooldown so the UI stays in sync
            const sec = getRetryAfterSeconds(e) ?? RESEND_COOLDOWN_SEC;
            if (e?.response?.status === 429) setCooldownSms(sec);
          }
        })();
      } else {
        // Respect remaining cooldown from previous send
        setCooldownSms(RESEND_COOLDOWN_SEC - elapsed);
      }
    }
  }, [navigate]);

  // ======= Cooldown tickers =======
  useEffect(() => {
    if (!cooldownEmail) return;
    const id = setInterval(() => setCooldownEmail((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldownEmail]);

  useEffect(() => {
    if (!cooldownSms) return;
    const id = setInterval(() => setCooldownSms((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldownSms]);

  // ======= When both are verified, show success + redirect countdown =======
  useEffect(() => {
    if (emailVerified && mobileVerified && !allVerified) {
      setAllVerified(true);
      setRedirectIn(5);
    }
  }, [emailVerified, mobileVerified, allVerified]);

  useEffect(() => {
    if (!allVerified) return;
    const tick = setInterval(() => setRedirectIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    const to = setTimeout(() => {
      localStorage.removeItem("mobileE164");
      navigate("/login");
    }, 5000);
    return () => {
      clearInterval(tick);
      clearTimeout(to);
    };
  }, [allVerified, navigate]);

  // ======= Derived UI bits =======
  const maskedEmail = useMemo(() => {
    if (!email) return "";
    const [user, domain] = email.split("@");
    if (!domain) return email;
    const u =
      user.length <= 2
        ? user[0] + "*"
        : user[0] + "*".repeat(Math.max(1, user.length - 2)) + user.slice(-1);
    return `${u}@${domain}`;
  }, [email]);

  const only6Digits = (v) => v.replace(/[\u0080-\uFFFF]/g, "").replace(/\D/g, "").slice(0, 6);

  // ======= Helpers =======
  function getRetryAfterSeconds(err) {
    const h = err?.response?.headers?.["retry-after"];
    if (!h) return null;
    const sec = Number(h);
    return Number.isFinite(sec) ? Math.max(0, sec) : null;
  }

  // ======= EMAIL: verify =======
  const handleVerifyEmail = async (e) => {
    e?.preventDefault?.();
    if (submittingEmail) return;
    setEmailMsg("");
    setSubmittingEmail(true);
    try {
      const clean = only6Digits(emailOtp);
      if (!/^[0-9]{6}$/.test(clean)) {
        setEmailMsg("Please enter a valid 6-digit OTP.");
        setSubmittingEmail(false);
        return;
      }
      await axios.post("/verify-otp", { email, otp: clean });
      setEmailVerified(true);
      setEmailMsg("Email verified ✓");
    } catch (err) {
      const errMsg = err.response?.data?.error || "Invalid or expired email OTP.";
      setEmailMsg(errMsg);
    } finally {
      setSubmittingEmail(false);
    }
  };

  // ======= EMAIL: resend (with 429 handling) =======
  const handleResendEmail = async () => {
    if (resendLoadingEmail || cooldownEmail > 0 || allVerified) return;
    setResendLoadingEmail(true);
    setEmailMsg("");
    try {
      await axios.post("/resend-signup-otp", { email });

      // Also refresh SMS in background (signup needs SMS)
      (async () => {
        try {
          const { data } = await axios.post("/send-mobile-otp", { email });
          if (data?.mobileE164) localStorage.setItem("mobileE164", data.mobileE164);
          localStorage.setItem(STORAGE_SMS_LAST_SENT, String(Date.now()));
        } catch (e) {
          console.warn("send-mobile-otp (resend bg) failed:", e?.response?.data || e?.message);
        }
      })();

      setEmailMsg("OTP resent to your email.");
      setCooldownEmail(RESEND_COOLDOWN_SEC);
    } catch (e) {
      if (e?.response?.status === 429) {
        const sec = getRetryAfterSeconds(e) ?? RESEND_COOLDOWN_SEC;
        setCooldownEmail(sec);
        setEmailMsg(`Please wait ${sec}s before requesting another email OTP.`);
      } else {
        setEmailMsg("Failed to resend OTP. Try again later.");
      }
    } finally {
      setResendLoadingEmail(false);
    }
  };

  // ======= MOBILE: verify =======
  const handleVerifyMobile = async () => {
    setMobileMsg("");
    try {
      const clean = only6Digits(mobileOtp);
      if (!/^[0-9]{6}$/.test(clean)) {
        setMobileMsg("Please enter a valid 6-digit mobile OTP.");
        return;
      }
      const mobile = (localStorage.getItem("mobileE164") || "").trim();
      if (!mobile) {
        setMobileMsg("Missing mobile number for verification. Tap Resend SMS first.");
        return;
      }
      const { data } = await axios.post("/verify-mobile-otp", { email, mobile, otp: clean });
      if (data?.ok) {
        setMobileVerified(true);
        setMobileMsg("Mobile verified ✓");
      } else {
        setMobileMsg("Invalid mobile OTP.");
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to verify mobile OTP.";
      setMobileMsg(msg);
    }
  };

  // ======= MOBILE: resend (with 429 handling) =======
  const handleResendSms = async () => {
    if (resendLoadingSms || cooldownSms > 0 || allVerified) return;
    setResendLoadingSms(true);
    setMobileMsg("");
    try {
      const { data } = await axios.post("/send-mobile-otp", { email });
      if (data?.mobileE164) localStorage.setItem("mobileE164", data.mobileE164);
      localStorage.setItem(STORAGE_SMS_LAST_SENT, String(Date.now()));
      setMobileMsg("SMS OTP resent.");
      setCooldownSms(RESEND_COOLDOWN_SEC);
    } catch (e) {
      if (e?.response?.status === 429) {
        const sec = getRetryAfterSeconds(e) ?? RESEND_COOLDOWN_SEC;
        setCooldownSms(sec);
        setMobileMsg(`Please wait ${sec}s before requesting another SMS OTP.`);
      } else {
        setMobileMsg(e?.response?.data?.error || "Failed to resend SMS OTP.");
      }
    } finally {
      setResendLoadingSms(false);
    }
  };

  // ======= Combined: Verify BOTH & Continue =======
  const canClickVerifyBoth =
    emailOtp.length === 6 && mobileOtp.length === 6 && !verifyingBoth && !submittingEmail && !allVerified;

  const handleVerifyBothAndContinue = async () => {
    if (!canClickVerifyBoth) return;

    setVerifyingBoth(true);
    setEmailMsg("");
    setMobileMsg("");

    const mobile = (localStorage.getItem("mobileE164") || "").trim();

    const tasks = [
      axios
        .post("/verify-otp", { email, otp: only6Digits(emailOtp) })
        .then(() => ({ emailOk: true }))
        .catch((err) => ({ emailOk: false, emailErr: err.response?.data?.error || "Invalid or expired email OTP." })),
      axios
        .post("/verify-mobile-otp", { email, mobile, otp: only6Digits(mobileOtp) })
        .then(() => ({ mobileOk: true }))
        .catch((err) => ({ mobileOk: false, mobileErr: err.response?.data?.error || "Invalid mobile OTP." })),
    ];

    const [emailRes, mobileRes] = await Promise.all(tasks);

    if (!emailRes.emailOk) setEmailMsg(emailRes.emailErr);
    else {
      setEmailVerified(true);
      setEmailMsg("Email verified ✓");
    }

    if (!mobileRes.mobileOk) setMobileMsg(mobileRes.mobileErr);
    else {
      setMobileVerified(true);
      setMobileMsg("Mobile verified ✓");
    }

    setVerifyingBoth(false);
    // Success banner + redirect handled by effect watching both verified flags
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 px-3 sm:px-4 py-6">
      {/* Autofill + dark-mode contrast fix */}
      <style>{`
        .dark input:-webkit-autofill,
        .dark input:-webkit-autofill:focus,
        .dark input:-webkit-autofill:hover {
          -webkit-text-fill-color: #e5e7eb; /* slate-200 */
          transition: background-color 5000s ease-in-out 0s;
          caret-color: #e5e7eb;
        }
        .dark input:-webkit-autofill::first-line { color: #e5e7eb; }
      `}</style>

      <motion.div
        className="w-full max-w-sm sm:max-w-md bg-white dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-lg p-4 sm:p-6 md:p-8"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-indigo-700 dark:text-indigo-300 mb-1.5 sm:mb-2">
          Verify Your Account
        </h2>
        <p className="text-center text-gray-700 dark:text-gray-300 text-xs sm:text-sm mb-5 sm:mb-6">
          We sent codes to <span className="font-medium">{maskedEmail}</span> and your phone.
        </p>

        {/* SUCCESS BANNER */}
        {allVerified && (
          <Banner ok>
            <div className="text-sm">
              <div className="font-semibold">Thank you for completing the verification.</div>
              <div>You can now log in with your Email and Password.</div>
              <div className="mt-1">
                Redirecting to the Login page in{" "}
                <span className="font-medium">{redirectIn}</span> second{redirectIn === 1 ? "" : "s"}…
              </div>
            </div>
          </Banner>
        )}

        {/* EMAIL OTP */}
        <section className="mb-6">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Email OTP</h3>

          {emailMsg && (
            <Banner ok={!/invalid|failed|error|expire|wrong|not/i.test(emailMsg)}>
              {emailMsg}
            </Banner>
          )}

          <form onSubmit={handleVerifyEmail} className="space-y-3">
            <OtpInput
              value={emailOtp}
              onChange={(v) => setEmailOtp(only6Digits(v))}
              placeholder="••••••"
              ariaLabel="6 digit email OTP"
            />
            <div className="flex items-center justify-between">
              <button
                type="submit"
                disabled={submittingEmail || emailOtp.length !== 6 || emailVerified || allVerified}
                className={`px-4 py-2 rounded-lg text-white text-sm font-semibold transition ${
                  submittingEmail || emailOtp.length !== 6 || emailVerified || allVerified
                    ? "bg-indigo-400 dark:bg-indigo-500 cursor-not-allowed"
                    : "bg-indigo-700 hover:bg-indigo-800 dark:bg-indigo-600 dark:hover:bg-indigo-700"
                }`}
              >
                {emailVerified ? "Verified ✓" : submittingEmail ? "Verifying…" : "Verify Email"}
              </button>

              <button
                type="button"
                onClick={handleResendEmail}
                disabled={resendLoadingEmail || cooldownEmail > 0 || allVerified}
                className={`text-xs sm:text-sm text-indigo-700 dark:text-indigo-400 hover:underline ${
                  resendLoadingEmail || cooldownEmail > 0 || allVerified ? "opacity-60 cursor-default" : ""
                }`}
              >
                {resendLoadingEmail ? "Resending…" : cooldownEmail > 0 ? `Resend in ${cooldownEmail}s` : "Resend Email"}
              </button>
            </div>
          </form>
        </section>

        {/* MOBILE OTP */}
        <section>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Mobile OTP (SMS)</h3>

          {mobileMsg && (
            <Banner ok={!/invalid|failed|error|expire|wrong|not/i.test(mobileMsg)}>
              {mobileMsg}
            </Banner>
          )}

          <div className="space-y-3">
            <OtpInput
              value={mobileOtp}
              onChange={(v) => setMobileOtp(only6Digits(v))}
              placeholder="••••••"
              ariaLabel="6 digit mobile OTP"
            />
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={handleVerifyMobile}
                disabled={mobileOtp.length !== 6 || mobileVerified || allVerified}
                className={`px-4 py-2 rounded-lg text-white text-sm font-semibold transition ${
                  mobileOtp.length !== 6 || mobileVerified || allVerified
                    ? "bg-indigo-400 dark:bg-indigo-500 cursor-not-allowed"
                    : "bg-indigo-700 hover:bg-indigo-800 dark:bg-indigo-600 dark:hover:bg-indigo-700"
                }`}
              >
                {mobileVerified ? "Verified ✓" : "Verify Mobile"}
              </button>

              <button
                type="button"
                onClick={handleResendSms}
                disabled={resendLoadingSms || cooldownSms > 0 || allVerified}
                className={`text-xs sm:text-sm text-indigo-700 dark:text-indigo-400 hover:underline ${
                  resendLoadingSms || cooldownSms > 0 || allVerified ? "opacity-60 cursor-default" : ""
                }`}
              >
                {resendLoadingSms ? "Resending…" : cooldownSms > 0 ? `Resend SMS in ${cooldownSms}s` : "Resend SMS"}
              </button>
            </div>
          </div>
        </section>

        {/* Verify BOTH & Continue */}
        {!allVerified && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={handleVerifyBothAndContinue}
              disabled={!canClickVerifyBoth}
              className={`w-full text-white text-sm sm:text-base font-semibold py-2.5 sm:py-3 rounded-lg transition ${
                canClickVerifyBoth && !verifyingBoth
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-emerald-400 cursor-not-allowed"
              }`}
            >
              {verifyingBoth ? "Verifying…" : "Verify both codes & continue"}
            </button>
          </div>
        )}

        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={() => navigate("/signup")}
            className="text-[11px] sm:text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline"
          >
            Use a different email
          </button>
        </div>
      </motion.div>
    </div>
  );
}

/* ---------- Reusable pieces ---------- */
function OtpInput({ value, onChange, placeholder, ariaLabel }) {
  const onPaste = (e) => {
    const pasted = (e.clipboardData?.getData("text") || "").trim();
    const digits = pasted.replace(/[\u0080-\uFFFF]/g, "").replace(/\D/g, "").slice(0, 6);
    if (digits) {
      e.preventDefault();
      onChange(digits);
    }
  };
  return (
    <input
      type="tel"
      inputMode="numeric"
      pattern="^[0-9]{6}$"
      maxLength={6}
      required
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onPaste={onPaste}
      className="w-full text-center tracking-widest text-lg sm:text-xl px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800/80 text-gray-900 dark:text-slate-100 placeholder-gray-500 outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500 focus:border-indigo-400 dark:focus:border-indigo-500 transition"
      placeholder={placeholder}
      aria-label={ariaLabel}
    />
  );
}

function Banner({ ok = true, children }) {
  return (
    <div
      className={`text-center text-xs sm:text-sm p-2.5 sm:p-3 mb-3 sm:mb-4 rounded-md border ${
        ok
          ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800/40"
          : "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/40"
      }`}
      role="alert"
      aria-live="polite"
    >
      {children}
    </div>
  );
}
